import Link from 'next/link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CheckCircle } from 'lucide-react'

interface ClientPricingCardsProps {
  buildLoginRedirect: (target: string) => string
  isVisible: boolean
}

export default function ClientPricingCards({ buildLoginRedirect, isVisible }: ClientPricingCardsProps) {
  return (
    <section
      className={`w-full bg-gradient-to-b from-slate-50 to-white px-4 py-16 sm:px-6 md:py-20 lg:px-8 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">무료로 시작하고 필요할 때 확장하세요</h2>
          <p className="mx-auto max-w-2xl text-base font-medium text-gray-900 md:text-lg">
            프로젝트 등록 3분, 요청 10회 무료
          </p>
          <p className="mx-auto max-w-2xl mt-2 text-sm text-gray-500">
            추가 요청이 필요할 때만 크레딧으로 확장할 수 있습니다
          </p>
        </div>

        <div
          className="
            flex gap-6 overflow-x-auto pb-2
            snap-x snap-mandatory scroll-smooth
            [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            md:grid md:grid-cols-2 md:overflow-visible md:pb-0 md:snap-none
          "
        >
          {/* Free Plan */}
          <Card className="flex h-full w-full shrink-0 snap-start flex-col rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:shadow-md md:w-auto md:shrink">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900">무료</CardTitle>
              <p className="mt-2 text-sm text-gray-600">처음 시작하는 기업/담당자에게 적합</p>
            </CardHeader>

            <CardContent className="flex h-full flex-1 flex-col space-y-4">
              {/* 핵심 배지 */}
              <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
                <p className="text-base font-bold text-blue-700">견적 요청 10회 제공</p>
              </div>

              <ul className="space-y-2.5">
                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#06f]" />
                  <span className="text-sm text-gray-700">프로젝트 등록/공개</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#06f]" />
                  <span className="text-sm text-gray-700">제안 수신(개인/팀)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#06f]" />
                  <span className="text-sm text-gray-700">기본 관리</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#06f]" />
                  <span className="text-sm font-semibold text-gray-900">10회 무료</span>
                </li>
              </ul>

              <div className="mt-auto pt-4">
                <Button asChild className="w-full bg-gray-900 text-white hover:bg-gray-800">
                  <Link href={buildLoginRedirect('/client/projects/new')}>무료로 견적 요청 시작하기</Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Credits Plan */}
          <Card className="relative flex h-full w-full shrink-0 snap-start flex-col rounded-2xl border-2 border-[#06f] bg-white shadow-lg transition-all hover:shadow-xl hover:-translate-y-1 md:w-auto md:shrink">
            <div className="absolute -top-3 right-4">
              <Badge className="bg-[#06f] text-white text-xs">추천</Badge>
            </div>

            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900">크레딧</CardTitle>
              <p className="mt-2 text-sm text-gray-600">필요한 만큼만 충전해 사용</p>
            </CardHeader>

            <CardContent className="flex h-full flex-1 flex-col space-y-4">
              {/* 핵심 배지 2개 - 단위/단가 강조 */}
              <div className="space-y-2">
                <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-2.5">
                  <p className="text-base font-bold text-blue-700">1요청 = 1크레딧</p>
                </div>
                <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-2.5">
                  <p className="text-base font-bold text-blue-700">1크레딧 = 100원</p>
                </div>
              </div>

              <ul className="space-y-2.5">
                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#06f]" />
                  <span className="text-sm text-gray-700">무료 기능 포함</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#06f]" />
                  <span className="text-sm text-gray-700">추가 견적 요청 가능</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#06f]" />
                  <span className="text-sm text-gray-700">구독 없이 건별 사용</span>
                </li>
              </ul>

              <div className="mt-auto pt-4">
                <Link
                  href={buildLoginRedirect('/my/client/billing')}
                  className="inline-flex items-center text-sm font-medium text-blue-600 transition-colors hover:text-blue-700 hover:underline"
                >
                  가격 보기
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </section>
  )
}

